// v2 — horizontal mini-stepper rendered above each appointment row.
// Shows where the appointment sits within its canonical workflow:
//   - Acquisition (Aufnahme): 2 steps  → meeting held → AV signed
//   - Viewing (Besichtigung): 5 steps → viewing → Schein → Kaufanbot → Notar → sold
//
// State of each step is computed at the call site (see workflowSteps.ts);
// this component only renders. Done = gold fill, Current = gold ring,
// Future = outline. Connector lines mirror the done state.

import { Check } from 'lucide-react';
import { useEffect, useState } from 'react';

export interface WorkflowStep {
  key: string;
  label: string;
  state: 'done' | 'current' | 'future';
}

interface Props {
  steps: WorkflowStep[];
  /** Drops the labels and shrinks dots — used inside dense row layouts. */
  compact?: boolean;
}

export function WorkflowStepper({ steps, compact = false }: Props) {
  // Mobile-friendly tap tooltip — tap a numbered dot to see what it
  // means. Auto-dismisses after 2.5 s. Desktop hover still works via
  // the native `title` attribute below.
  const [openKey, setOpenKey] = useState<string | null>(null);
  useEffect(() => {
    if (!openKey) return;
    const id = setTimeout(() => setOpenKey(null), 2500);
    return () => clearTimeout(id);
  }, [openKey]);

  return (
    <ol
      className={`flex items-center gap-1 ${compact ? 'flex-nowrap overflow-x-auto' : 'flex-wrap'}`}
      aria-label="Workflow steps"
    >
      {steps.map((s, i) => {
        const isLast = i === steps.length - 1;
        const dot = compact ? 18 : 22;
        const stateLabel =
          s.state === 'done'
            ? '✓ '
            : s.state === 'current'
              ? '➜ '
              : '';
        return (
          <li key={s.key} className="flex items-center gap-1">
            <div className="relative flex flex-col items-center">
              <button
                type="button"
                title={`${stateLabel}${s.label}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenKey((k) => (k === s.key ? null : s.key));
                }}
                aria-label={`${stateLabel}${s.label}`}
                className={`inline-flex items-center justify-center rounded-full transition focus:outline-none focus:ring-2 focus:ring-[hsl(var(--brand-gold))]/40 ${
                  s.state === 'done'
                    ? 'bg-[hsl(var(--brand-gold))] text-white'
                    : s.state === 'current'
                      ? 'bg-card ring-2 ring-[hsl(var(--brand-gold-deep))] text-[hsl(var(--brand-gold-deep))]'
                      : 'bg-card border border-line text-ink-3'
                }`}
                style={{ width: dot, height: dot, fontSize: compact ? 9 : 11 }}
              >
                {s.state === 'done' ? (
                  <Check className={compact ? 'h-3 w-3' : 'h-3.5 w-3.5'} />
                ) : (
                  <span className="font-semibold">{i + 1}</span>
                )}
              </button>
              {!compact && (
                <span
                  className={`mt-1 text-[10px] uppercase tracking-[0.06em] ${
                    s.state === 'future' ? 'text-ink-3' : 'text-ink-2'
                  } ${s.state === 'current' ? 'font-semibold text-[hsl(var(--brand-gold-deep))]' : ''}`}
                >
                  {s.label}
                </span>
              )}
              {openKey === s.key && (
                <span
                  role="tooltip"
                  className="pointer-events-none absolute -top-7 left-1/2 z-20 -translate-x-1/2 whitespace-nowrap rounded-md bg-ink px-2 py-1 text-[10px] font-medium text-paper shadow-elevated"
                  style={{ animation: 'fm-fade-in 0.15s ease-out' }}
                >
                  {stateLabel}{s.label}
                  <span
                    className="absolute left-1/2 top-full -translate-x-1/2 border-4 border-transparent border-t-ink"
                    aria-hidden
                  />
                </span>
              )}
            </div>
            {!isLast && (
              <span
                className={`h-px transition ${
                  s.state === 'done'
                    ? 'bg-[hsl(var(--brand-gold))]'
                    : 'bg-line'
                }`}
                style={{ width: compact ? 14 : 28 }}
                aria-hidden
              />
            )}
          </li>
        );
      })}
    </ol>
  );
}
